import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackOfflineModeActivated, trackProgressQueueSynced } from '../utils/speakrootsAnalytics.js';
import offlineManager from '../utils/offlineManager.js';
import logger from '../utils/logger';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineData, setOfflineData] = useState({});
  const [syncStatus, setSyncStatus] = useState({
    isOnline: navigator.onLine,
    syncInProgress: false,
    hasDatabase: false,
    queueStatus: { totalItems: 0, failedItems: 0, pendingItems: 0 }
  });

  // Update sync status periodically
  useEffect(() => {
    const updateSyncStatus = async () => {
      const status = offlineManager.getOfflineStatus();
      const queueStatus = await offlineManager.getSyncQueueStatus();
      
      setSyncStatus({
        ...status,
        queueStatus
      });
    };

    // Update immediately
    updateSyncStatus();

    // Update every 30 seconds
    const interval = setInterval(updateSyncStatus, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      
      // Update sync status
      const status = offlineManager.getOfflineStatus();
      const queueStatus = await offlineManager.getSyncQueueStatus();
      setSyncStatus({ ...status, queueStatus });
      
      // Track sync when coming back online
      const queuedItems = queueStatus.totalItems;
      if (queuedItems > 0) {
        trackProgressQueueSynced(queuedItems);
      }
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      // Track offline mode activated
      trackOfflineModeActivated();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const saveOfflineData = (key, data) => {
    setOfflineData(prev => ({
      ...prev,
      [key]: data
    }));
    
    // Also save to localStorage for persistence
    try {
      localStorage.setItem(`offline_${key}`, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save offline data:', error);
    }
  };

  const getOfflineData = (key) => {
    // First check in-memory data
    if (offlineData[key]) {
      return offlineData[key];
    }
    
    // Then check localStorage
    try {
      const stored = localStorage.getItem(`offline_${key}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to retrieve offline data:', error);
      return null;
    }
  };

  const clearOfflineData = (key) => {
    setOfflineData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
    
    try {
      localStorage.removeItem(`offline_${key}`);
    } catch (error) {
      console.warn('Failed to clear offline data:', error);
    }
  };

  const forceSync = async () => {
    const success = await offlineManager.forceSync();
    if (success) {
      // Update sync status after sync
      const status = offlineManager.getOfflineStatus();
      const queueStatus = await offlineManager.getSyncQueueStatus();
      setSyncStatus({ ...status, queueStatus });
    }
    return success;
  };

  const clearFailedSyncItems = async () => {
    const success = await offlineManager.clearFailedSyncItems();
    if (success) {
      // Update sync status after clearing
      const status = offlineManager.getOfflineStatus();
      const queueStatus = await offlineManager.getSyncQueueStatus();
      setSyncStatus({ ...status, queueStatus });
    }
    return success;
  };

  const value = {
    isOffline,
    offlineData,
    syncStatus,
    saveOfflineData,
    getOfflineData,
    clearOfflineData,
    forceSync,
    clearFailedSyncItems
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};


